import React, { Component, CSSProperties } from 'react';
import Chips from 'react-chips';
import cx from 'classnames';
import { Tag } from 'antd';
import { colors } from '../../../styles/variables';
import { SxTooltip } from '../utils/wrapComponents';

import styles from './index.module.scss';

interface IChipsProps {
  values: string[];
  placeholder?: string;
  setValues: (values: string[]) => void;
  maxValuesAllowed?: number;
  withReadOnly?: boolean;
  verifyValues?: (values: string[]) => boolean;
  errorMessage?: string;
  disabled?: boolean;
  tooltipMessage?: string;
}

interface ITheme {
  chipsContainer: CSSProperties;
  container: CSSProperties;
}

interface IReactChip {
  state: {
    value: string;
  };
  onBlur(): void;
}

interface IState {
  error?: string;
}

const KEY_CODES = [32, 188, 13]; // key codes for space bar, comma, enter

export class SxChips extends Component<IChipsProps, IState> {
  theme: ITheme;
  disabledTheme: ITheme;
  variablesInput: React.RefObject<IReactChip>;

  constructor(props: IChipsProps) {
    super(props);
    this.state = {
      error: undefined
    };
    this.theme = {
      chipsContainer: {
        alignItems: 'center',
        border: `1px solid ${colors.gray300}`,
        display: 'flex',
        flexWrap: 'wrap',
        fontSize: '14px',
        fontFamily: 'Source Sans Pro',
        minHeight: 30,
        position: 'relative',
        borderRadius: '6px'
      },
      container: {
        flex: 1
      }
    };
    this.disabledTheme = {
      ...this.theme,
      chipsContainer: {
        ...this.theme.chipsContainer,
        pointerEvents: 'none'
      }
    };
    this.variablesInput = React.createRef();
    this.renderChips = this.renderChips.bind(this);
    this.onSaving = this.onSaving.bind(this);
    this.onCloseTag = this.onCloseTag.bind(this);
    this.isValidForSaving = this.isValidForSaving.bind(this);
    this.onClickCloseIcon = this.onClickCloseIcon.bind(this);
  }

  componentDidMount(): void {
    this.setState({ error: this.props.errorMessage });
    const currentRef = this.variablesInput.current;
    if (!currentRef) {
      return;
    }
    currentRef.onBlur = () => {
      this.setState({ error: undefined });
      if (!currentRef.state.value) {
        return;
      }
      const { values = [], setValues } = this.props;
      const newChips = [...values];
      newChips.push(currentRef.state.value);
      currentRef.state.value = '';
      if (this.isValidForSaving(newChips)) {
        setValues(newChips);
      }
    };
  }
  componentDidUpdate(prevProps: IChipsProps): void {
    if (!this.state.error && this.props.errorMessage) {
      this.setState({ error: this.props.errorMessage });
    }
    if (this.state.error && !this.props.errorMessage && prevProps.errorMessage) {
      this.setState({ error: undefined });
    }
  }

  private isValidForSaving(values: string[]) {
    const { withReadOnly, maxValuesAllowed, verifyValues } = this.props;
    const lengthExceeded = maxValuesAllowed && values.length > maxValuesAllowed;
    this.setState({ error: lengthExceeded ? 'Max of items allowed exceeded' : undefined });
    return !((withReadOnly && !values.length) || lengthExceeded || (verifyValues && !verifyValues(values)));
  }

  private readonly onSaving = (values: string[]) => {
    const { setValues } = this.props;
    if (this.isValidForSaving(values)) {
      setValues(values);
    }
  };

  private readonly onCloseTag = (value: string) => {
    const { setValues, values } = this.props;
    const remainingValues = values.filter((tag) => tag !== value);
    setValues(remainingValues);
  };

  private onClickCloseIcon(isReadOnly: boolean, value: string, event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    if (!isReadOnly) {
      this.onCloseTag(value);
    }
  }

  private renderChips(value: string) {
    const { values, disabled, withReadOnly = false, tooltipMessage = '' } = this.props;
    const isReadOnly = disabled || (withReadOnly && values[0] === value);
    const tag = <Tag
      className={cx({ [styles.customStyle]: !isReadOnly, [styles.readOnly]: isReadOnly })}
      closable
      onClose={this.onClickCloseIcon.bind(this, isReadOnly, value)}
      color={colors.baseWhite}
    >
      {value}
    </Tag>;
    if (isReadOnly) {
      return <SxTooltip Component={tag} tooltipText={tooltipMessage} classes={[styles.tooltip]} />;
    }
    return tag;
  }

  render(): JSX.Element {
    const { placeholder = 'Write here', values, disabled } = this.props;
    const { error } = this.state;

    return (
      <div className={styles.container}>
        <div className={styles.field}>
          <Chips
            ref={this.variablesInput}
            value={values}
            createChipKeys={KEY_CODES}
            placeholder={values.length ? '' : placeholder}
            theme={disabled ? this.disabledTheme : this.theme}
            onChange={this.onSaving}
            renderChip={(value: string) => this.renderChips(value)}
          />
        </div>
        {error && <span>{error}</span>}
      </div>
    );
  }
}
